import React, { useRef, useEffect, useState } from "react";
import { VISITOR_API_URL } from '../config';
import { getBrowserInfo } from '../utils/browserDetect';

function Dosha() {
  const sectionRef = useRef(null);
  const hasCalledRef = useRef(false);

  const [showForm, setShowForm] = useState(false);
  // const [isFormFilled, setIsFormFilled] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");

  const validateName = (name) => {
    const nameRegex = /^[A-Za-z\s]{2,50}$/;
    return nameRegex.test(name.trim());
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    return emailRegex.test(email.trim());
  };

  const scrollToSection = () => {
    sectionRef.current.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {

    if (hasCalledRef.current) return;

    hasCalledRef.current = true;

    const createVisitor = async () => {
      try {
        const jscd = getBrowserInfo();
        // Get UTM parameters from URL
        // const urlParams = new URLSearchParams(window.location.search);

        const requestBody = {
          type: "insert",
          browsername: jscd.browser,
          browserversion: jscd.browser + ' ' + jscd.browserVersion,
          operatingsystem: jscd.os + ' ' + jscd.osVersion,
          device: jscd.device,
          sourceurl: window.location.href,
          productid: "",
          location: "",
          utmsource: "",
          utmmedium: "",
          utmcampaign: "",
          utmterm: "",
          utmcontent: "",
          visitortype: ""
        };
        const response = await fetch(VISITOR_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });
        const data = await response.json();
        if (data.success && data.visitor_id) {
          sessionStorage.setItem("visitor_id", data.visitor_id);
        }
      } catch (error) {
        console.error('Error creating visitor:', error);
      }
    };

    createVisitor();
  }, []); // Empty dependency array means this runs once on mount

  /* ---------------------------------------------------
  UPDATE PDF COUNT
--------------------------------------------------- */
  const DownloadPDF = async () => {
    try {
      const visitor_id = sessionStorage.getItem("visitor_id");
      if (!visitor_id) return;

      await fetch(VISITOR_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "update",
          name: name,
          email: email,
          visitor_id: visitor_id
        })
      });
    } catch (err) {
      console.error("PDF update failed", err);
    }
  };

  /* ---------------------------------------------------
    DOWNLOAD BUTTON HANDLER (KEY LOGIC)
 --------------------------------------------------- */
  const handleDownloadClick = async () => {
    // FIRST CLICK → SHOW FORM
    if (!showForm) {
      setShowForm(true);
      return;
    }

    // SECOND CLICK → VALIDATE FORM
    setNameError("");
    setEmailError("");

    let isValid = true;

    if (!validateName(name)) {
      setNameError("Please enter valid name.");
      isValid = false;
    }

    if (!validateEmail(email)) {
      setEmailError("Please enter valid email address.");
      isValid = false;
    }

    if (!isValid) {
      return;
    }

    // Lead downloaded PDF
    await DownloadPDF();

    // DOWNLOAD PDF
    const link = document.createElement("a");
    link.href = require("../assets/images/tools/Toolkit.pdf");
    link.download = "Ikigai365-Toolkit.pdf";
    link.click();

    // RESET FORM & HIDE
    setName("");
    setEmail("");
    setNameError("");
    setEmailError("");
    setShowForm(false);
  };

  return (
    <main className="tools">
      <section className="section-01">
        <div className="wrapper">
          <div className="content">
            <h1 className="heading">What if you had a quiet <span>ibasho</span></h1>
            <h2 className="subheading">- a small space to be yourself at your own pace?</h2>
            <p className="sub-text">“myIkigai365 Nourishment Journal help you create that gentle space.”</p>
            <p className="text">In the rush of everyday life, we often forget to pause and reconnect with ourselves.
              These tools offer you that small pause — simple practices that help you slow down, notice what’s
              unfolding inside you, and tune into the moments that bring energy, ease, and a sense of being
              alive.</p>
            <p className="text">Each tool in <span>‘This is Ikigai365’</span> is crafted through our workshops and
              our lived experiences across Japan, Singapore and India. They are intentionally simple: easy to
              begin, comforting to return to, and personal enough to make your day feel a little lighter and
              more grounded. Whether you have five quiet minutes or a stretch of time on the weekend, these
              reflections help you settle into your own rhythm.</p>
            <p className="text">Here on the website, you’ll find few of our Signature tools to begin with — just
              enough to start sensing your own everyday <span>reasons to feel alive and fulfilled</span>. To
              explore deeper reflections, guided exercises, and the full myIkigai365 Nourishment Journal,
              you’ll find all of them inside <span>This is Ikigai365</span>.</p>
            <button type="button" className="btn btn-primary" onClick={scrollToSection}>
              <span className="material-icons-outlined">flight_takeoff</span>Explore the Tools
            </button>
          </div>
        </div>
      </section>
      <section className="section-02">
        <div className="wrapper">
          <div className="content">
            <h2 className="heading">Selfcare using the Clinical Dohsa Therapy</h2>
            <p className="subheading">By Prof. Akihiro Hasegawa</p>
            <video className="frame" controls preload="metadata" playsinline>
              <source src="https://d1t93dcqtxsdcn.cloudfront.net" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <p className="text"><span>Dohsa,</span> in Japanese, means <span>“moving the body.”</span> It describes in psychological view the full arc of movement — from forming an intention in the mind, to strive, to the body finally responding.</p>
            <p className="text">Clinical <span>Dohsa-hou</span> uses this mind–body connection to help people harness a sense of self-control: the feeling that <span>“I can move myself by myself, with my own will.”</span> It supports the mind gently through the body, especially when psychological support is needed.</p>
            <p className="text">When <span>Prof. Hasegawa</span> devised the Three Elements of Ikigai Model, he wove this very feeling of self-generated movement and self-agency into the experience of Ikigai. In this tool, we guide you in using Dohsa-hou as a simple form of self-care — a way to tune into your own intention, movement, and inner agency in daily life.</p>
            <h4 className="refheading">References:</h4>
            <ul className="reftext">
              <li>Kobayashi, S., 1966. Rehabilitation of cerebral palsy. In: G. Naruse, ed. Educational hypnotism. Tokyo: Seishinshobo, pp.279–290. (in Japanese)</li>
              <li>Naruse, G., 1995. Basic clinical movement science. Tokyo: Gakuensha. (in Japanese)</li>
              <li>Okamoto, K., Kobayashi, Y. & Hasegawa, A. (eds.), 2023. Brief psychotherapy for self-care: Meditation techniques, NLP, clinical Dohsa-hou, biofeedback. Kanagawa: Shunpusha. (in Japanese).</li>
            </ul>
          </div>
        </div>
      </section>
      <section className="section-03" ref={sectionRef}>
        <div className="wrapper">
          <div className="_left">
            <div className="content">
              <h4 className="heading">Coffee Date with Yourself</h4>
              <p className="text">A simple weekly pause to sit with yourself — the way you would with a trusted friend. In 10–15 minutes, you note your feelings, revisit the week’s experiences, and capture any small insights that arise. This gentle check-in helps you sense what lifted you, what drained you, and what you want to carry forward — all at your own <span>myPace</span>.</p>
              {showForm && (
                <div className="leadform">
                  <form className="form">
                    <div className="form-group">
                      <label htmlFor="fname">Full Name</label>
                      <input type="text" className="form-control" id="fname" placeholder="Enter your name" value={name}
                        onChange={(e) => {
                          setName(e.target.value);
                          if (nameError) setNameError("");
                        }} />
                      {nameError && <p style={{ color: "red", fontSize: "12px", marginTop: "5px" }}>{nameError}</p>}
                    </div>
                    <div className="form-group">
                      <label htmlFor="lname">Email Address</label>
                      <input type="email" className="form-control" id="lname" placeholder="Enter your Email adderess" value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (emailError) setEmailError("");
                        }} />
                      {emailError && <p style={{ color: "red", fontSize: "12px", marginTop: "5px" }}>{emailError}</p>}
                    </div>
                  </form>
                </div>
              )}
              <button onClick={handleDownloadClick} className="btn btn-primary">
                <span className="material-icons-outlined">get_app</span>Download the Tool
              </button>
            </div>
          </div>
          <div className="_right">
            <img src={require("../assets/images/tools/3.jpg")} className="frame" alt="frame" />
          </div>
        </div>
      </section>

    </main>
  );
}

export default Dosha;
